/**
 * 运营两轴作用域：语言必填 × App（全部=该语言全局 / 某 App=可选覆盖）。
 *
 * 来源完全取服务端列表 envelope 的 config_source；浏览器不维护平行会话身份。
 */

export const OPS_MODULES = [
  { module: 'home_sections', label: '首页推荐分区标题', kind: '文案' },
  { module: 'weekly_featured', label: '首页每周精选书籍', kind: '选品' },
  { module: 'shelf_recommendations', label: '书架推荐', kind: '选品' },
  { module: 'banner_slides', label: '首页 Banner', kind: '文案 + 选品' },
  { module: 'popup_scenes', label: '运营弹窗', kind: '上下架' },
  { module: 'faq', label: 'FAQ', kind: '文案' },
] as const;

export type OpsModule = (typeof OPS_MODULES)[number]['module'];
export type OpsScopeKind = 'GLOBAL' | 'NO_OVERRIDE' | 'OVERRIDE';

export const SCOPE_META: Record<OpsScopeKind, { label: string; cls: string }> = {
  GLOBAL: { label: '该语言全局', cls: 'chip-info' },
  NO_OVERRIDE: { label: '无覆盖', cls: 'chip-warn' },
  OVERRIDE: { label: '已覆盖', cls: 'chip-pos' },
};

export function opsModuleLabel(module: OpsModule): string {
  return OPS_MODULES.find((m) => m.module === module)?.label ?? module;
}

/** 与 createAppDedicatedConfig 可执行切片对齐。 */
export function usesAppDedicatedCopy(module: OpsModule): boolean {
  return (
    module === 'home_sections' ||
    module === 'faq' ||
    module === 'shelf_recommendations' ||
    module === 'banner_slides'
  );
}

export function isOpsGlobal(appId: string): boolean {
  return !appId || appId === 'all';
}

/** 85 §1.2：越 App.allowed_languages 时复制入口禁用（接口仍兜底 APP_LANGUAGE_NOT_ALLOWED）。 */
export function isOpsCopyLanguageAllowed(
  appLangs: readonly string[] | undefined,
  lang: string,
  appId: string,
): boolean {
  if (isOpsGlobal(appId)) return true;
  return (appLangs ?? []).includes(lang);
}

/**
 * 直建（A2 §0.4 `init_mode='empty'`）只受 `allowed_languages` 约束。
 *
 * 复制的「全局源就绪」前置对它不适用 —— 没有源要搬，故 `globalN === 0` 不禁用本入口，
 * 否则「全局层为空即无法建覆盖」这条死结原样留着（85 §3.1）。
 */
export function opsDirectCreateButtonDisabled(languageAllowed: boolean): boolean {
  return !languageAllowed;
}

export function opsDirectCreateButtonTitle(
  languageAllowed: boolean,
  lang: string,
): string | undefined {
  if (!languageAllowed) return `${lang.toUpperCase()} 不在该 App 的运行语言集内`;
  return undefined;
}

export function opsCopyButtonDisabled(globalN: number, languageAllowed: boolean): boolean {
  return globalN === 0 || !languageAllowed;
}

export function opsCopyButtonTitle(
  globalN: number,
  languageAllowed: boolean,
  lang: string,
): string | undefined {
  if (!languageAllowed) return `该语言不在本 App 投放语言内，无法复制 ${lang.toUpperCase()} 配置`;
  if (globalN === 0) return '该语言全局为空（0 条），没有可复制的源';
  return undefined;
}

export function deriveOpsScope(
  _module: OpsModule,
  appId: string,
  _lang: string,
  serverHas: boolean,
): OpsScopeKind {
  if (isOpsGlobal(appId)) return 'GLOBAL';
  return serverHas ? 'OVERRIDE' : 'NO_OVERRIDE';
}
